import { Alert, Button, Form, Input, InputNumber, Select, Tooltip } from "antd";
import type { FormInstance, FormListFieldData, FormListOperation } from "antd";
import { InfoIcon, XIcon } from "lucide-react";
import { useEffect } from "react";

type Child = { id: string; category: "cwb" | "cnb" | "infant" | "unassigned"; age: number | null };
const categoryOptions = [
  { value: "unassigned", label: "Choose hotel category" },
  { value: "cwb", label: "With bed" },
  { value: "cnb", label: "Without bed" },
  { value: "infant", label: "Infant" },
];

/** Collects optional ages in a permanently mounted list, retaining surplus passenger records. */
export function ChildPassengersForm({
  form,
  onOperations,
}: {
  form: FormInstance;
  onOperations?: (operations: FormListOperation) => void;
}) {
  return (
    <Form.List name="childPassengers">
      {(fields, operations) => (
        <ChildRows form={form} fields={fields} operations={operations} onOperations={onOperations} />
      )}
    </Form.List>
  );
}

function ChildRows({
  form,
  fields,
  operations,
  onOperations,
}: {
  form: FormInstance;
  fields: FormListFieldData[];
  operations: FormListOperation;
  onOperations?: (operations: FormListOperation) => void;
}) {
  const cwb = Form.useWatch("cwb", { form, preserve: true }) || 0;
  const cnb = Form.useWatch("cnb", { form, preserve: true }) || 0;
  const infant = Form.useWatch("infant", { form, preserve: true }) || 0;
  const children = (Form.useWatch("childPassengers", form) || []) as Child[];
  useEffect(() => {
    onOperations?.(operations);
  }, [onOperations, operations]);
  useEffect(() => {
    const current = (form.getFieldValue("childPassengers") || []) as Child[];
    for (const [category, required] of [
      ["cwb", cwb],
      ["cnb", cnb],
      ["infant", infant],
    ] as const) {
      const present = current.filter((child) => child?.category === category).length;
      for (let i = present; i < Number(required); i++) operations.add({ id: crypto.randomUUID(), category, age: null });
    }
  }, [cwb, cnb, infant, form, operations, children.map((child) => child?.category).join(",")]);
  const seen = { cwb: 0, cnb: 0, infant: 0 };
  const required = { cwb: Number(cwb), cnb: Number(cnb), infant: Number(infant) };
  return (
    <div className="space-y-2">
      {fields.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-6">
          <span className="font-medium text-slate-700">
            Child ages <span className="font-normal text-slate-400">(optional)</span>
          </span>
          <span>Completed years</span>
          <Tooltip title="Enter each child's age for the trip. Hotel categories remain independent of age.">
            <button
              type="button"
              aria-label="About child ages"
              className="inline-flex rounded text-slate-400 focus-visible:outline-2"
            >
              <InfoIcon size={14} />
            </button>
          </Tooltip>
        </div>
      )}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,250px),1fr))] gap-2">
        {fields.map((field, index) => {
          const child = children[field.name];
          const surplus =
            child?.category && (child.category === "unassigned" || ++seen[child.category] > required[child.category]);
          return (
            <div
              key={field.key}
              className={`rounded-lg border p-2 ${surplus ? "border-amber-200 bg-amber-50/50" : "border-slate-200 bg-white"}`}
            >
              <div className="flex items-center gap-2">
                <Form.Item name={[field.name, "id"]} hidden>
                  <Input />
                </Form.Item>
                <span
                  aria-label={`Child ${index + 1}`}
                  className="flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-500"
                >
                  {index + 1}
                </span>
                <Form.Item name={[field.name, "category"]} className="mb-0! min-w-0 flex-1">
                  <Select
                    aria-label={`Child ${index + 1} hotel category`}
                    options={categoryOptions}
                    className="w-full"
                    popupMatchSelectWidth={false}
                  />
                </Form.Item>
                <Form.Item name={[field.name, "age"]} className="mb-0! w-20 shrink-0">
                  <InputNumber
                    aria-label={`Child ${index + 1} age for the trip`}
                    min={0}
                    precision={0}
                    placeholder="Age"
                    //suffix={<span className="text-xs text-slate-400">y</span>}
                    className="w-full!"
                  />
                </Form.Item>
              </div>
              {surplus && (
                <div className="mt-1 flex items-center justify-between gap-1 text-xs text-amber-700">
                  <span>Excluded from autofill</span>
                  <Tooltip title="Remove surplus record">
                    <Button
                      type="text"
                      size="small"
                      aria-label={`Remove surplus child ${index + 1}`}
                      icon={<XIcon size={14} />}
                      onClick={() => operations.remove(field.name)}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {children.length > Number(cwb) + Number(cnb) + Number(infant) && (
        <Alert
          type="warning"
          showIcon
          message="Passenger counts decreased. Surplus records are retained so known ages are not lost."
        />
      )}
    </div>
  );
}
